import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  Checkbox,
  Icon,
  IconButton,
  Segmented,
  Sheet,
  Spinner,
  Switch,
  Tag,
  ToneIcon,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { shellApi } from "../../api/shellApi";
import type { TodayDto, TodayMoneyBlock, TodayTaskDto } from "../../api/nextApi";
import { EVENTS, notifyDataChanged, onEvent } from "../events";
import { useShell } from "../shell/ShellContext";
import {
  loadToday,
  onDoneChange,
  readAutoOpen,
  readDone,
  takeFirstVisitToday,
  todayBadge,
  useTodayState,
  writeAutoOpen,
  writeDone,
} from "./todayStore";
import "./today.css";

const ils = (agorot: number | null | undefined) =>
  `₪${((Number(agorot) || 0) / 100).toLocaleString("he-IL", { maximumFractionDigits: 0 })}`;
const count = (n: number, one: string, many: string) => (Number(n) === 1 ? one : `${Number(n || 0).toLocaleString("he-IL")} ${many}`);
const dayDate = (day: string) => new Date(`${day}T12:00:00`);
const fmtDay = (day: string, opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "numeric" }) => dayDate(day).toLocaleDateString("he-IL", opts);
const weekday = (day: string) => dayDate(day).toLocaleDateString("he-IL", { weekday: "long" });

/** 9 → ט׳, 15 → ט״ו, 5787 → תשפ״ז */
function gematria(n: number): string {
  const ones = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
  const tens = ["", "י", "כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ"];
  const hundreds = ["", "ק", "ר", "ש", "ת", "תק", "תר", "תש", "תת", "תתק"];
  let v = n % 1000;
  let s = hundreds[Math.floor(v / 100)];
  v %= 100;
  if (v === 15) s += "טו";
  else if (v === 16) s += "טז";
  else s += tens[Math.floor(v / 10)] + ones[v % 10];
  return s.length === 1 ? `${s}׳` : `${s.slice(0, -1)}״${s.slice(-1)}`;
}

function hebrewDate(day: string): string {
  try {
    const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", { day: "numeric", month: "long", year: "numeric" }).formatToParts(dayDate(day));
    const get = (t: string) => parts.find((p) => p.type === t)?.value;
    const d = Number(get("day"));
    const y = Number(get("year"));
    const m = get("month");
    if (!d || !y || !m) return "";
    return `${gematria(d)} ב${m.replace(/^ב/u, "")} ${gematria(y)}`;
  } catch {
    return "";
  }
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "לילה טוב";
  if (h < 12) return "בוקר טוב";
  if (h < 17) return "צהריים טובים";
  if (h < 21) return "ערב טוב";
  return "לילה טוב";
}

/** "היום" — in the top bar's end slot (the top-left corner in RTL). */
export function TodayButton() {
  const { userId } = useTodayUser();
  const snap = useTodayState(true);
  const doneSig = useSyncExternalStore(onDoneChange, () => JSON.stringify(Array.from(readDone(userId, snap.data?.date || ""))), () => "[]");
  const badge = todayBadge(snap.data, new Set(JSON.parse(doneSig) as string[]));
  const label = badge ? `היום בתוכנה — ${badge} לטיפול` : "היום בתוכנה";
  return (
    <button type="button" className="tdy-btn" onClick={() => window.dispatchEvent(new CustomEvent(EVENTS.today))} aria-label={label} title={label}>
      <Icon name="sun" size={17} />
      <span className="tdy-btn__label">היום</span>
      {badge ? <span className="tdy-btn__badge">{badge > 99 ? "99+" : badge}</span> : null}
    </button>
  );
}

let currentUser: { userId: string | null; name: string } = { userId: null, name: "" };
export function setTodayUser(userId: string | null, name: string) {
  currentUser = { userId, name };
}
function useTodayUser() {
  return currentUser;
}

/**
 * TodayPanel — "היום בתוכנה": a side sheet with what came in today, what needs a decision,
 * the day's tasks and the meetings ahead. Mounted once by the shell; opens via openToday().
 */
export function TodayPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useCkToast();
  const { userId, name } = useTodayUser();
  const { can } = useShell();
  const snap = useTodayState(true);
  const data = snap.data;
  const today = data?.date || new Date().toISOString().slice(0, 10);
  const doneSig = useSyncExternalStore(onDoneChange, () => JSON.stringify(Array.from(readDone(userId, today))), () => "[]");
  const done = new Set(JSON.parse(doneSig) as string[]);
  const [open, setOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(() => readAutoOpen(userId));

  useEffect(
    () =>
      onEvent(EVENTS.today, () => {
        setOpen(true);
        void loadToday();
      }),
    [],
  );
  useEffect(() => {
    if (!data?.date || !readAutoOpen(userId)) return;
    if (takeFirstVisitToday(userId, data.date)) setOpen(true);
  }, [data?.date, userId]);
  useEffect(() => setOpen(false), [location.pathname]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };
  const toggleDone = (key: string) => {
    const next = new Set(done);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    writeDone(userId, today, next);
  };
  const completeTask = async (t: TodayTaskDto) => {
    try {
      await shellApi.updateTask(t.id, { status: "completed" });
      toast.success("המשימה סומנה כבוצעה");
      notifyDataChanged("task");
    } catch (e) {
      toast.error((e as { message?: string })?.message || "לא הצלחנו לעדכן את המשימה");
    }
  };

  return (
    <Sheet open={open} onClose={() => setOpen(false)} width={520} ariaLabel="היום בתוכנה" className="tdy">
      <header className="tdy__head">
        <span className="tdy__logo">
          <Icon name="sun" size={20} />
        </span>
        <div className="tdy__titles">
          <b>היום בתוכנה</b>
          <small>
            {weekday(today)}, {fmtDay(today, { day: "numeric", month: "long" })}
            {hebrewDate(today) ? ` · ${hebrewDate(today)}` : ""}
          </small>
        </div>
        <IconButton icon="refresh" label="רענון" onClick={() => void loadToday(true)} disabled={snap.loading} />
        <IconButton icon="x" label="סגירה" onClick={() => setOpen(false)} />
      </header>

      <div className="tdy__body">
        {snap.error && !data ? (
          <Banner tone="danger">
            {snap.error}{" "}
            <button type="button" className="tdy__link" onClick={() => void loadToday(true)}>
              לנסות שוב
            </button>
          </Banner>
        ) : null}
        {!data ? (
          <div className="tdy__busy">
            <Spinner size={26} />
          </div>
        ) : (
          <TodayContent data={data} done={done} name={name} can={can} onGo={go} onToggleDone={toggleDone} onCompleteTask={completeTask} />
        )}
      </div>

      <footer className="tdy__foot">
        <Switch
          checked={autoOpen}
          onChange={(v) => {
            setAutoOpen(v);
            writeAutoOpen(userId, v);
          }}
          label="לפתוח את המסך הזה בכניסה הראשונה בכל יום"
        />
        {data?.generatedAt ? (
          <small>עודכן ב-{new Date(data.generatedAt).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}</small>
        ) : null}
      </footer>
    </Sheet>
  );
}

function Stat({ tone, icon, label, value, sub, onClick }: { tone: string; icon: string; label: string; value: string; sub: string; onClick?: () => void }) {
  return (
    <button type="button" className={`tdy__stat tdy__stat--${tone}`} onClick={onClick} disabled={!onClick}>
      <span className="tdy__stat-top">
        <Icon name={icon} size={15} />
        {label}
      </span>
      <b>{value}</b>
      <small>{sub}</small>
    </button>
  );
}

function Section({ title, icon, n, children }: { title: string; icon: string; n?: number; children: ReactNode }) {
  return (
    <section className="tdy__sec">
      <h3>
        <Icon name={icon} size={16} />
        {title}
        {n ? <span className="tdy__count">{n}</span> : null}
      </h3>
      {children}
    </section>
  );
}

function MoneyList({ title, tone, block, onAll }: { title: string; tone: string; block: TodayMoneyBlock; onAll?: () => void }) {
  return (
    <div className="tdy__money">
      <div className="tdy__money-head">
        <span className={`tdy__dot tdy__dot--${tone}`} />
        <b>{title}</b>
        <span className="tdy__money-total">{`${ils(block.amountAgorot)} · ${count(block.count, "תנועה אחת", "תנועות")}`}</span>
        {onAll ? (
          <button type="button" className="tdy__link" onClick={onAll}>
            הכל
          </button>
        ) : null}
      </div>
      {block.items.length ? (
        <ul className="tdy__money-list">
          {block.items.slice(0, 5).map((r) => (
            <li key={r.id}>
              <span className="tdy__money-name">{r.name || "ללא שם"}</span>
              <span className="tdy__money-amt">{ils(r.amountAgorot)}</span>
            </li>
          ))}
          {block.count > 5 ? <li className="tdy__money-more">{`ועוד ${block.count - 5}…`}</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

const PRIORITY_TAG: Record<string, { tone: "danger" | "warning"; label: string }> = {
  urgent: { tone: "danger", label: "דחוף" },
  high: { tone: "warning", label: "גבוהה" },
};

function TodayContent({
  data,
  done,
  name,
  can,
  onGo,
  onToggleDone,
  onCompleteTask,
}: {
  data: TodayDto;
  done: Set<string>;
  name: string;
  can: (k: string) => boolean;
  onGo: (to: string) => void;
  onToggleDone: (key: string) => void;
  onCompleteTask: (t: TodayTaskDto) => Promise<void>;
}) {
  const [tab, setTab] = useState<"mine" | "general">("mine");
  const [pending, setPending] = useState<Set<string>>(() => new Set());
  const { money, attention, tasks, meetings, customers } = data;
  const open = attention.filter((a) => !done.has(a.key));
  const handled = attention.filter((a) => done.has(a.key));
  const list = tasks ? (tab === "mine" ? tasks.mine : tasks.general) : [];
  const complete = async (t: TodayTaskDto) => {
    setPending((s) => new Set(s).add(t.id));
    await onCompleteTask(t);
    setPending((s) => {
      const n = new Set(s);
      n.delete(t.id);
      return n;
    });
  };
  const firstName = name.split(" ")[0] || "";

  return (
    <div className="tdy__content">
      <p className="tdy__hello">
        {greeting()}
        {firstName ? `, ${firstName}` : ""}!{" "}
        {open.length || tasks?.mine.length
          ? `יש ${count(open.length + (tasks?.mine.length ?? 0), "דבר אחד", "דברים")} לטיפול היום.`
          : "הכל רגוע — אין דברים פתוחים לטיפול."}
      </p>

      <div className="tdy__stats">
        {money.payments ? (
          <Stat tone="success" icon="coin" label="תשלומים היום" value={ils(money.payments.amountAgorot)} sub={count(money.payments.count, "תשלום אחד", "תשלומים")} onClick={can("subscriptions") ? () => onGo("/subscriptions") : undefined} />
        ) : null}
        {money.incomes ? (
          <Stat tone="primary" icon="wallet" label="הכנסות היום" value={ils(money.incomes.amountAgorot)} sub={count(money.incomes.count, "הכנסה אחת", "הכנסות")} onClick={can("income_expenses") ? () => onGo("/income-expenses") : undefined} />
        ) : null}
        {money.expenses ? (
          <Stat tone="danger" icon="wallet" label="הוצאות היום" value={ils(money.expenses.amountAgorot)} sub={count(money.expenses.count, "הוצאה אחת", "הוצאות")} onClick={can("income_expenses") ? () => onGo("/income-expenses") : undefined} />
        ) : null}
        {customers ? (
          <Stat tone="warning" icon="users" label="לקוחות חדשים" value={String(customers.newToday)} sub={`${customers.newWeek.toLocaleString("he-IL")} השבוע`} onClick={can("customers") ? () => onGo("/customers") : undefined} />
        ) : null}
      </div>
      {money.paymentsMonthAgorot != null ? <p className="tdy__hint">מתחילת החודש: {ils(money.paymentsMonthAgorot)} בתשלומים</p> : null}

      {open.length || handled.length ? (
        <Section title="דורש החלטה" icon="flag" n={open.length}>
          {open.map((a) => (
            <div key={a.key} className="tdy__action">
              <ToneIcon icon={a.icon || "alert"} tone={a.tone} size={30} />
              <span className="tdy__row-main">
                <b>{a.title}</b>
                <small>
                  {count(a.count, "פריט אחד", "פריטים")}
                  {a.amountAgorot ? ` · ${ils(a.amountAgorot)}` : ""}
                </small>
              </span>
              <Button size="sm" onClick={() => onGo(a.path)}>
                {a.label}
              </Button>
              <button type="button" className="tdy__done-btn" onClick={() => onToggleDone(a.key)} title="להסתיר עד מחר">
                <Icon name="check" size={14} />
                טופל
              </button>
            </div>
          ))}
          {handled.length ? (
            <details className="tdy__done">
              <summary>{count(handled.length, "סומן אחד כטופל", "סומנו כטופלו")}</summary>
              {handled.map((a) => (
                <div key={a.key} className="tdy__action is-handled">
                  <ToneIcon icon={a.icon || "alert"} tone={a.tone} size={30} />
                  <span className="tdy__row-main">
                    <b>{a.title}</b>
                  </span>
                  <button type="button" className="tdy__done-btn" onClick={() => onToggleDone(a.key)}>
                    <Icon name="undo" size={14} />
                    החזרה
                  </button>
                </div>
              ))}
            </details>
          ) : null}
        </Section>
      ) : null}

      {tasks ? (
        <Section title="משימות להיום" icon="task">
          <Segmented
            ariaLabel="סוג משימות"
            value={tab}
            onChange={(v) => setTab(v as "mine")}
            items={[
              { value: "mine", label: `שלי (${tasks.mine.length})` },
              { value: "general", label: `של הצוות (${tasks.general.length})` },
            ]}
          />
          {tab === "general" && tasks.counts.unassigned ? (
            <p className="tdy__hint">{`${count(tasks.counts.unassigned, "משימה אחת לא משויכת", "משימות לא משויכות")} — כדאי לשייך לאחד מאנשי הצוות.`}</p>
          ) : null}
          <ul className="tdy__list">
            {list.map((t) => (
              <li key={t.id} className={`tdy__task ${t.overdue ? "is-overdue" : ""}`.trim()}>
                <Checkbox checked={pending.has(t.id)} disabled={pending.has(t.id)} onChange={() => void complete(t)} aria-label="סימון כבוצע" />
                <span className="tdy__row-main">
                  <b>{t.title}</b>
                  <small>
                    {t.overdue ? <Tag tone="danger">באיחור</Tag> : null}
                    {PRIORITY_TAG[t.priority] ? <Tag tone={PRIORITY_TAG[t.priority].tone}>{PRIORITY_TAG[t.priority].label}</Tag> : null}
                    {t.status === "in_progress" ? <Tag tone="primary">בטיפול</Tag> : null}
                    {t.dueAt ? <span>{new Date(t.dueAt).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}</span> : null}
                    {tab === "general" ? <span>{t.assigneeName || "לא משויכת"}</span> : null}
                    {t.customerName ? <span>{t.customerName}</span> : null}
                  </small>
                </span>
              </li>
            ))}
          </ul>
          {!list.length ? (
            <p className="tdy__hint">
              {tab === "mine" ? "אין לך משימות להיום" : "אין משימות של הצוות להיום"}
              {tab === "mine" && tasks.counts.upcomingMine ? ` · ${count(tasks.counts.upcomingMine, "משימה אחת", "משימות")} בשבוע הקרוב` : ""}
            </p>
          ) : null}
          <div className="tdy__acts">
            <Button size="sm" variant="ghost" icon="list" onClick={() => onGo("/tasks")}>
              לכל המשימות
            </Button>
          </div>
        </Section>
      ) : null}

      {meetings && meetings.length ? (
        <Section title="ישיבות קרובות" icon="calendar" n={meetings.length}>
          <ul className="tdy__list">
            {meetings.map((m) => (
              <li key={m.id} className="tdy__task">
                <ToneIcon icon="users" tone="violet" size={22} />
                <button type="button" className="tdy__row-main tdy__row-btn" onClick={() => onGo(`/meetings?meeting=${m.id}`)}>
                  <b>{m.title}</b>
                  <small>
                    {m.scheduledAt
                      ? new Date(m.scheduledAt).toLocaleString("he-IL", { weekday: "short", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "ללא מועד"}
                  </small>
                </button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {money.payments && money.payments.count ? (
        <Section title="נכנס היום" icon="coin">
          <MoneyList title="תשלומים" tone="success" block={money.payments} onAll={can("subscriptions") ? () => onGo("/subscriptions") : undefined} />
          {money.incomes && money.incomes.count ? <MoneyList title="הכנסות" tone="primary" block={money.incomes} onAll={() => onGo("/income-expenses")} /> : null}
        </Section>
      ) : null}
    </div>
  );
}

export default TodayPanel;
